import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Camera,
  CheckCircle2,
  Clock3,
  Languages,
  Shield,
  Users,
} from "lucide-react";
import Seo from "../components/Seo";
import { isPrerendered } from "../lib/hydration";
import { BUSINESS } from "../lib/businessInfo";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?auto=format&fit=crop&q=80&w=2000";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const softIn = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const benefits = [
  {
    icon: Users,
    title: "Reach students worldwide",
    description:
      "List online and prenatal yoga offerings where people already compare courses, teachers, and schedules.",
  },
  {
    icon: BadgeCheck,
    title: "Build a trusted profile",
    description:
      "Add credentials, photo, expertise, and classes. Profiles are reviewed before going live so students can book with confidence.",
  },
  {
    icon: BookOpen,
    title: "Publish your offerings",
    description:
      "Share live online classes, prenatal sessions, and course formats with clear pricing and schedules.",
  },
  {
    icon: Languages,
    title: "Share the languages you teach in",
    description:
      "List every language you speak fluently so students can find teachers who match how they learn and communicate.",
  },
  {
    icon: Shield,
    title: "Verified instructor standards",
    description:
      "Certificate uploads and admin review help keep the marketplace authentic for teachers and students.",
  },
];

const steps = [
  {
    title: "Create your instructor account",
    detail:
      "Sign up with your name and email, then verify your inbox so we know you are a real teacher.",
  },
  {
    title: "Build your yoga teacher profile",
    detail:
      "Add basics, languages you teach in, teaching expertise, certifications with proof, a clear photo, and at least one online or prenatal offering students can book.",
  },
  {
    title: "Submit for review",
    detail:
      "Preview your public profile and submit. We typically review instructor applications within about 2 business days.",
  },
  {
    title: "Get published for students",
    detail:
      "Once approved, your instructor profile and classes appear on Yoga Retreats Booking for students comparing online and prenatal yoga teachers.",
  },
];

const TEACHING_LANGUAGES = [
  "English",
  "Hindi",
  "Sanskrit",
  "Spanish",
  "French",
  "German",
  "Portuguese",
  "Italian",
  "Dutch",
  "Swedish",
  "Norwegian",
  "Danish",
  "Mandarin",
  "Japanese",
  "Korean",
  "Arabic",
  "Russian",
  "Tamil",
  "Telugu",
  "Kannada",
  "Malayalam",
  "Bengali",
  "Marathi",
  "Gujarati",
  "Punjabi",
];

const faqs = [
  {
    q: "Who can become an instructor on Yoga Retreats Booking?",
    a: "Individual yoga teachers offering online courses or prenatal yoga can apply. Retreat schools and operators should use Become a partner instead of the instructor flow.",
  },
  {
    q: "What do I need for approval?",
    a: "A complete profile with a clear photo, the languages you teach in, teaching expertise, at least one certification with proof, and one priced offering with a schedule.",
  },
  {
    q: "How long does review take?",
    a: "We typically review instructor applications within 2 business days and email you when a decision is ready.",
  },
  {
    q: "Is my profile public before approval?",
    a: "No. Draft and under-review profiles stay private. Only approved instructors are published on public teacher and course pages.",
  },
  {
    q: "Can I edit my profile after approval?",
    a: "Yes. You can update offerings and most profile details from your dashboard. Sensitive changes like name or photo may need a quick re-review.",
  },
];

const TITLE = "Become a Yoga Instructor Online";
const DESCRIPTION =
  "Join Yoga Retreats Booking as an online or prenatal yoga instructor. Create a verified profile, publish courses, and reach students worldwide.";

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: TITLE,
      url: "https://yogaretreatsbooking.com/become-an-instructor",
      description: DESCRIPTION,
      isPartOf: {
        "@type": "WebSite",
        name: BUSINESS.tradeName,
        url: "https://yogaretreatsbooking.com",
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://yogaretreatsbooking.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Become an instructor",
          item: "https://yogaretreatsbooking.com/become-an-instructor",
        },
      ],
    },
    {
      "@type": "HowTo",
      name: "How to become an online yoga instructor on Yoga Retreats Booking",
      description: DESCRIPTION,
      totalTime: "P3D",
      step: steps.map((step, index) => ({
        "@type": "HowToStep",
        position: index + 1,
        name: step.title,
        text: step.detail,
        url: "https://yogaretreatsbooking.com/become-an-instructor#how-onboarding-works",
      })),
    },
    {
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.a,
        },
      })),
    },
  ],
};

export default function BecomeInstructorPage() {
  return (
    <div
      className="min-h-screen pt-16"
      style={{
        "--bi-forest": "#0c3b2e",
        "--bi-moss": "#1a5c45",
        "--bi-leaf": "#2f8f6b",
        "--bi-mist": "#e8f3ee",
        "--bi-honey": "#e8c47c",
        "--bi-honey-deep": "#c9a24a",
        "--bi-ink": "#12261f",
        "--bi-muted": "#4d655c",
        background:
          "linear-gradient(180deg, #f4faf7 0%, #ffffff 35%, #f7fbf9 70%, #eef7f2 100%)",
        fontFamily: "'Outfit', 'Inter', sans-serif",
      }}
    >
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,650;9..144,700&family=Outfit:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <Seo
        title={TITLE}
        description={DESCRIPTION}
        canonicalPath="/become-an-instructor"
        jsonLd={jsonLd}
      />

      {/* Hero — one composition, brand-first, full-bleed image */}
      <section className="relative min-h-[88vh] overflow-hidden flex items-end sm:items-center">
        <motion.div
          className="absolute inset-0"
          initial={isPrerendered ? false : "hidden"}
          animate="visible"
          variants={softIn}
        >
          <img
            src={HERO_IMAGE}
            alt="Yoga instructor teaching an outdoor class"
            className="h-full w-full object-cover"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(115deg, rgba(12,59,46,0.92) 0%, rgba(12,59,46,0.78) 42%, rgba(26,92,69,0.45) 70%, rgba(12,59,46,0.35) 100%)",
            }}
          />
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "radial-gradient(ellipse 80% 50% at 80% 20%, rgba(232,196,124,0.28), transparent 55%)",
            }}
          />
        </motion.div>

        <div className="relative w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <motion.div
            initial={isPrerendered ? false : "hidden"}
            animate="visible"
            variants={fadeUp}
            custom={0}
          >
            <p
              className="text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-white mb-5 sm:mb-6"
              style={{ fontFamily: "'Fraunces', Georgia, serif" }}
            >
              {BUSINESS.tradeName}
            </p>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-medium text-white/95 max-w-xl leading-snug mb-4">
              Teach online. Reach students who are ready to practice.
            </h1>
            <p className="text-base sm:text-lg max-w-lg mb-8 leading-relaxed" style={{ color: "rgba(232,243,238,0.88)" }}>
              Create a verified instructor profile, publish online or prenatal offerings, and grow
              with a marketplace built for authentic teaching.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-semibold transition-transform hover:scale-[1.02]"
                style={{
                  background: "var(--bi-honey)",
                  color: "var(--bi-forest)",
                  borderRadius: "0.5rem",
                }}
              >
                Sign up as instructor <ArrowRight size={16} />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-semibold text-white/95 transition-colors hover:bg-white/10"
                style={{
                  border: "1px solid rgba(255,255,255,0.35)",
                  borderRadius: "0.5rem",
                }}
              >
                Sign in
              </Link>
            </div>
            <p className="mt-3 text-sm" style={{ color: "rgba(232,243,238,0.75)" }}>
              New teacher? Choose <span className="font-semibold text-white/90">Sign up</span>.
              Already registered? Choose <span className="font-semibold text-white/90">Sign in</span>.
            </p>
            <motion.p
              className="mt-6 text-sm inline-flex items-center gap-2"
              style={{ color: "rgba(232,196,124,0.95)" }}
              initial={isPrerendered ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55, duration: 0.5 }}
            >
              <Clock3 size={14} /> Review within ~2 business days
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Why join — open layout, soft color fields (not card grid) */}
      <section className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <motion.div
          initial={isPrerendered ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mb-14 max-w-2xl"
        >
          <p
            className="text-sm font-semibold uppercase tracking-[0.18em] mb-3"
            style={{ color: "var(--bi-leaf)" }}
          >
            Why instructors join
          </p>
          <h2
            className="text-3xl sm:text-4xl font-semibold leading-tight mb-4"
            style={{ fontFamily: "'Fraunces', Georgia, serif", color: "var(--bi-ink)" }}
          >
            A calm path from profile to published teacher
          </h2>
          <p className="text-base leading-relaxed" style={{ color: "var(--bi-muted)" }}>
            Onboarding built for trust, discoverability, and clear student-facing profiles — without
            the noise of a generic marketplace listing.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-10">
          {benefits.map((item, i) => (
            <motion.div
              key={item.title}
              initial={isPrerendered ? false : "hidden"}
              whileInView="visible"
              viewport={{ once: true }}
              custom={i}
              variants={fadeUp}
              className="relative pl-5"
              style={{ borderLeft: "2px solid var(--bi-honey)" }}
            >
              <item.icon className="mb-3" size={22} style={{ color: "var(--bi-moss)" }} />
              <h3
                className="text-lg font-semibold mb-2"
                style={{ color: "var(--bi-ink)" }}
              >
                {item.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--bi-muted)" }}>
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Languages you teach in */}
      <section
        id="languages"
        className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20"
      >
        <div
          className="rounded-3xl px-6 py-10 sm:px-10 sm:py-12 overflow-hidden relative"
          style={{
            background:
              "linear-gradient(135deg, rgba(232,243,238,0.95) 0%, rgba(255,255,255,0.98) 45%, rgba(232,196,124,0.18) 100%)",
            border: "1px solid rgba(26,92,69,0.12)",
          }}
        >
          <motion.div
            initial={isPrerendered ? false : "hidden"}
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            className="max-w-2xl mb-8"
          >
            <p
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] mb-3"
              style={{ color: "var(--bi-leaf)" }}
            >
              <Languages size={16} /> Languages you teach in
            </p>
            <h2
              className="text-3xl sm:text-4xl font-semibold leading-tight mb-4"
              style={{ fontFamily: "'Fraunces', Georgia, serif", color: "var(--bi-ink)" }}
            >
              Help students find teachers who speak their language
            </h2>
            <p className="text-base leading-relaxed" style={{ color: "var(--bi-muted)" }}>
              During onboarding you select every language you can teach in. Your choices appear on
              your public instructor profile so students can match classes to how they learn.
              English and Hindi are common, and regional or international languages are welcome.
            </p>
          </motion.div>

          <motion.div
            initial={isPrerendered ? false : "hidden"}
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={1}
            className="flex flex-wrap gap-2 mb-8"
            aria-label="Example languages instructors can select"
          >
            {TEACHING_LANGUAGES.map((lang) => (
              <span
                key={lang}
                className="rounded-full px-3 py-1.5 text-sm font-medium"
                style={{
                  background: "rgba(255,255,255,0.9)",
                  border: "1px solid rgba(26,92,69,0.16)",
                  color: "var(--bi-ink)",
                }}
              >
                {lang}
              </span>
            ))}
            <span
              className="rounded-full px-3 py-1.5 text-sm font-medium"
              style={{
                background: "rgba(26,92,69,0.08)",
                border: "1px dashed rgba(26,92,69,0.28)",
                color: "var(--bi-moss)",
              }}
            >
              + other languages you speak
            </span>
          </motion.div>

          <motion.div
            initial={isPrerendered ? false : "hidden"}
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={2}
            className="flex flex-wrap items-center gap-4"
          >
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-transform hover:scale-[1.02]"
              style={{
                background: "var(--bi-forest)",
                color: "#fff",
                borderRadius: "0.5rem",
              }}
            >
              Sign up &amp; add your languages <ArrowRight size={16} />
            </Link>
            <p className="text-sm" style={{ color: "var(--bi-muted)" }}>
              You&apos;ll choose languages in the <strong>Basics</strong> step of instructor onboarding.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How it works — moss panel with numbered flow */}
      <section
        id="how-onboarding-works"
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(160deg, var(--bi-forest) 0%, var(--bi-moss) 55%, #246b52 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 10% 80%, rgba(232,196,124,0.35), transparent 40%), radial-gradient(circle at 90% 10%, rgba(232,243,238,0.15), transparent 35%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <motion.div
            initial={isPrerendered ? false : "hidden"}
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl sm:text-4xl font-semibold text-white mb-3"
              style={{ fontFamily: "'Fraunces', Georgia, serif" }}
            >
              How onboarding works
            </h2>
            <p className="max-w-xl mx-auto" style={{ color: "rgba(232,243,238,0.82)" }}>
              Finish your listing checklist, preview how students will see you, then submit for review.
            </p>
          </motion.div>

          <ol className="grid md:grid-cols-4 gap-8 md:gap-6">
            {steps.map((step, index) => (
              <motion.li
                key={step.title}
                initial={isPrerendered ? false : "hidden"}
                whileInView="visible"
                viewport={{ once: true }}
                custom={index}
                variants={fadeUp}
                className="relative"
              >
                <span
                  className="block text-4xl font-semibold mb-3"
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    color: "var(--bi-honey)",
                  }}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "rgba(232,243,238,0.75)" }}>
                  {step.detail}
                </p>
              </motion.li>
            ))}
          </ol>

          <div
            className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm"
            style={{ color: "rgba(232,243,238,0.85)" }}
          >
            <span className="inline-flex items-center gap-2">
              <Languages size={16} style={{ color: "var(--bi-honey)" }} /> Teaching languages
            </span>
            <span className="inline-flex items-center gap-2">
              <Camera size={16} style={{ color: "var(--bi-honey)" }} /> Clear profile photo
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 size={16} style={{ color: "var(--bi-honey)" }} /> Certificate upload
            </span>
            <span className="inline-flex items-center gap-2">
              <BookOpen size={16} style={{ color: "var(--bi-honey)" }} /> At least one offering
            </span>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <nav aria-label="Related pages" className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
          <Link to="/signup" className="font-medium underline" style={{ color: "var(--bi-moss)" }}>
            Sign up as instructor
          </Link>
          <Link to="/online-courses" className="font-medium underline" style={{ color: "var(--bi-moss)" }}>
            Browse online yoga courses
          </Link>
          <Link to="/prenatal-yoga" className="font-medium underline" style={{ color: "var(--bi-moss)" }}>
            Browse prenatal yoga
          </Link>
        </nav>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <h2
          className="text-3xl sm:text-4xl font-semibold text-center mb-10"
          style={{ fontFamily: "'Fraunces', Georgia, serif", color: "var(--bi-ink)" }}
        >
          Instructor FAQ
        </h2>
        <div className="space-y-0">
          {faqs.map((faq, i) => (
            <motion.div
              key={faq.q}
              initial={isPrerendered ? false : "hidden"}
              whileInView="visible"
              viewport={{ once: true }}
              custom={i}
              variants={fadeUp}
              className="py-6"
              style={{
                borderBottom:
                  i === faqs.length - 1 ? "none" : "1px solid rgba(26,92,69,0.12)",
              }}
            >
              <h3 className="font-semibold mb-2" style={{ color: "var(--bi-ink)" }}>
                {faq.q}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--bi-muted)" }}>
                {faq.a}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, #0c3b2e 0%, #1a5c45 40%, #0f4a38 70%, #163d2f 100%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 60% 80% at 50% 120%, rgba(232,196,124,0.35), transparent 55%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 py-16 sm:py-20 text-center">
          <motion.div
            initial={isPrerendered ? false : "hidden"}
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2
              className="text-3xl sm:text-4xl font-semibold text-white mb-4"
              style={{ fontFamily: "'Fraunces', Georgia, serif" }}
            >
              Ready to teach with us?
            </h2>
            <p className="mb-8 max-w-md mx-auto" style={{ color: "rgba(232,243,238,0.85)" }}>
              Start your instructor application and publish a profile students can trust.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-semibold transition-transform hover:scale-[1.02]"
              style={{
                background: "var(--bi-honey)",
                color: "var(--bi-forest)",
                borderRadius: "0.5rem",
              }}
            >
              Sign up as instructor <ArrowRight size={16} />
            </Link>
            <p className="mt-4 text-sm" style={{ color: "rgba(232,243,238,0.75)" }}>
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-white underline underline-offset-2 hover:opacity-90">
                Sign in
              </Link>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
